import React, { useEffect, useState } from "react";
import { apiUrl } from "../config/config";
import OrderModal from "./OrderModal";
import SEO from "./SEO";

const getUserId = () => {
  const user = localStorage.getItem("user");
  if (!user) return null;
  try {
    const parsed = JSON.parse(user);
    return parsed?.id || null;
  } catch {
    return null;
  }
};

const Account = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOrder, setModalOrder] = useState(null);

  useEffect(() => {
    const userId = getUserId();
    if (!userId) {
      setLoading(false);
      return;
    }

    fetch(`${apiUrl}/api/orders/user/${userId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setOrders(data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Fetch orders error:", err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-lightPastelPurple px-4 py-20 text-center">
        <p className="text-lg font-extrabold text-darkPurple">
          Loading orders...
        </p>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="My Account"
        description="Shirts Patches"
        keywords="Shirts Patches"
      />

      <div className="min-h-screen bg-lightPastelPurple px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          {/* PAGE HEADER */}
          <div className="relative mb-10 overflow-hidden rounded-[34px] border border-pastelPurple bg-white px-6 py-10 text-center shadow-[0_22px_60px_rgba(47,34,84,0.10)] md:px-10">
            <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-pastelPurple/70 blur-3xl"></div>
            <div className="absolute -right-24 -bottom-24 h-80 w-80 rounded-full bg-keyLime/25 blur-3xl"></div>

            <div className="relative">
              <h2 className="text-4xl font-extrabold leading-tight tracking-tight text-darkPurple md:text-5xl">
                My Orders
              </h2>

              <div className="mx-auto mt-4 flex w-full max-w-[230px] items-center justify-center gap-3">
                <span className="h-[1px] flex-1 bg-[#d8c8ff]"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-keyLime shadow-[0_0_0_5px_rgba(215,255,79,0.25)]"></span>
                <span className="h-[1px] flex-1 bg-[#d8c8ff]"></span>
              </div>
            </div>
          </div>

          {orders.length === 0 ? (
            <div className="relative overflow-hidden rounded-[30px] border border-pastelPurple bg-white p-8 text-center shadow-[0_18px_45px_rgba(47,34,84,0.08)]">
              <div className="absolute left-0 top-0 h-72 w-72 rounded-full bg-pastelPurple/60 blur-3xl"></div>
              <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-keyLime/20 blur-3xl"></div>

              <p className="relative text-base font-bold leading-8 text-[#5f5870] md:text-lg">
                No orders found.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-[30px] border border-pastelPurple bg-white shadow-[0_18px_45px_rgba(47,34,84,0.08)]">
              <div className="hidden grid-cols-12 border-b border-pastelPurple bg-darkPurple px-6 py-4 text-sm font-extrabold text-white md:grid">
                <div className="col-span-2">Order ID</div>
                <div className="col-span-3">Name</div>
                <div className="col-span-2">Total</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Date</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>

              <div className="divide-y divide-pastelPurple">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className="grid grid-cols-1 gap-4 px-5 py-5 transition hover:bg-lightPastelPurple/60 md:grid-cols-12 md:items-center md:px-6"
                  >
                    <div className="md:col-span-2">
                      <p className="text-xs font-extrabold uppercase tracking-wide text-themePurple md:hidden">
                        Order ID
                      </p>
                      <p className="font-bold text-darkPurple">{order.id}</p>
                    </div>

                    <div className="md:col-span-3">
                      <p className="text-xs font-extrabold uppercase tracking-wide text-themePurple md:hidden">
                        Name
                      </p>
                      <p className="font-bold text-darkPurple">
                        {order.first_name} {order.last_name}
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <p className="text-xs font-extrabold uppercase tracking-wide text-themePurple md:hidden">
                        Total
                      </p>
                      <p className="font-extrabold text-[#5f7f00]">
                        ${order.total}
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <p className="text-xs font-extrabold uppercase tracking-wide text-themePurple md:hidden">
                        Status
                      </p>
                      <span className="inline-flex rounded-full border border-pastelPurple bg-lightPastelPurple px-3 py-1 text-xs font-extrabold capitalize text-darkPurple">
                        {order.payment_status}
                      </span>
                    </div>

                    <div className="md:col-span-2">
                      <p className="text-xs font-extrabold uppercase tracking-wide text-themePurple md:hidden">
                        Date
                      </p>
                      <p className="text-sm font-medium leading-6 text-[#5f5870]">
                        {new Date(order.created_at).toLocaleString()}
                      </p>
                    </div>

                    <div className="md:col-span-1 md:text-right">
                      <button
                        className="inline-flex rounded-xl bg-keyLime px-4 py-2 text-sm font-extrabold text-darkPurple shadow-[0_10px_22px_rgba(215,255,79,0.30)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#c8f531]"
                        onClick={() => setModalOrder(order)}
                      >
                        Preview
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {modalOrder && (
            <OrderModal order={modalOrder} onClose={() => setModalOrder(null)} />
          )}
        </div>
      </div>
    </>
  );
};

export default Account;